import { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';

const Navbar = () => {
  const [scrolled, setScrolled] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const location = useLocation();
  const { user, signOut, loading } = useAuth();

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 30);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    setMenuOpen(false);
  }, [location.pathname]);

  return (
    <header
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${
        scrolled ? 'bg-ea-black/95 backdrop-blur-md border-b border-ea-dark-border' : 'bg-transparent'
      }`}
    >
      <div className="max-w-7xl mx-auto px-6 lg:px-12 h-24 flex items-center justify-between">
        {/* Logo */}
        <Link to="/" className="flex items-center gap-3 cursor-pointer">
          <div className="w-16 h-16 flex items-center justify-center">
            <img
              src="https://static.readdy.ai/image/bcf9f2411fadd279f7d1fd38d7e2d7e8/6cfd408f22ccc1f303d260d7c847c128.png"
              alt="Everything Automation Logo"
              className="w-16 h-16 object-contain"
            />
          </div>
          <span className="text-white font-bold text-xl whitespace-nowrap tracking-tight">
            Everything <span className="text-ea-blue">Automation</span>
          </span>
        </Link>

        {/* Desktop Nav */}
        <nav className="hidden md:flex items-center gap-8">
          {[
            { label: 'Home', path: '/' },
            { label: 'About', path: '/about' },
            { label: 'Industries', path: '/industries' },
            { label: 'Pricing', path: '/pricing' },
            { label: 'Contact', path: '/contact' },
          ].map((link) => (
            <Link
              key={link.path}
              to={link.path}
              className={`text-sm font-medium transition-colors duration-200 whitespace-nowrap cursor-pointer ${
                location.pathname === link.path ? 'text-ea-blue' : 'text-white/70 hover:text-white'
              }`}
            >
              {link.label}
            </Link>
          ))}
        </nav>

        {/* CTA */}
        <div className="hidden md:flex items-center gap-4">
          {!loading && user ? (
            <>
              <Link
                to="/dashboard"
                className="px-5 py-2.5 bg-ea-blue/10 hover:bg-ea-blue/20 text-ea-blue text-sm font-medium rounded-lg border border-ea-blue/30 hover:border-ea-blue/60 transition-all duration-300 whitespace-nowrap cursor-pointer flex items-center gap-2"
              >
                <i className="ri-dashboard-3-line text-sm"></i>
                My Dashboard
              </Link>
              <button
                onClick={signOut}
                className="px-5 py-2.5 bg-transparent hover:bg-ea-dark-card text-white/70 hover:text-white text-sm font-medium rounded-lg border border-ea-dark-border hover:border-ea-blue/40 transition-all duration-300 whitespace-nowrap cursor-pointer"
              >
                Sign Out
              </button>
            </>
          ) : (
            <>
              <Link
                to="/signup"
                className="px-5 py-2.5 bg-transparent hover:bg-ea-dark-card text-white/70 hover:text-white text-sm font-medium rounded-lg border border-ea-dark-border hover:border-ea-blue/40 transition-all duration-300 whitespace-nowrap cursor-pointer"
              >
                Sign In
              </Link>
              <Link
                to="/contact"
                className="px-5 py-2.5 bg-ea-blue hover:bg-ea-blue-light text-white text-sm font-semibold rounded-lg transition-all duration-300 whitespace-nowrap cursor-pointer flex items-center gap-2"
              >
                Book a Demo
                <i className="ri-arrow-right-line text-sm"></i>
              </Link>
            </>
          )}
        </div>

        {/* Mobile Hamburger */}
        <button
          className="md:hidden w-10 h-10 flex items-center justify-center text-white cursor-pointer"
          onClick={() => setMenuOpen(!menuOpen)}
          aria-label="Toggle menu"
        >
          <i className={`text-xl ${menuOpen ? 'ri-close-line' : 'ri-menu-3-line'}`}></i>
        </button>
      </div>

      {/* Mobile Menu */}
      {menuOpen && (
        <div className="md:hidden bg-ea-black border-t border-ea-dark-border">
          {/* Mobile Menu Brand Header */}
          <div className="flex flex-col items-center justify-center py-6 px-6 border-b border-ea-dark-border/60 bg-ea-navy/60">
            <img
              src="https://static.readdy.ai/image/bcf9f2411fadd279f7d1fd38d7e2d7e8/6cfd408f22ccc1f303d260d7c847c128.png"
              alt="Everything Automation Logo"
              className="w-20 h-20 object-contain mb-3"
            />
            <span className="text-white font-bold text-xl tracking-tight">
              Everything <span className="text-ea-blue">Automation</span>
            </span>
            <span className="text-ea-gray text-xs mt-1">AI Automation for UK Businesses</span>
          </div>

          {/* Nav Links */}
          <div className="px-6 py-4 space-y-1">
            {[
              { label: 'Home', path: '/', icon: 'ri-home-4-line' },
              { label: 'About', path: '/about', icon: 'ri-information-line' },
              { label: 'Industries', path: '/industries', icon: 'ri-building-2-line' },
              { label: 'Pricing', path: '/pricing', icon: 'ri-price-tag-3-line' },
              { label: 'Contact', path: '/contact', icon: 'ri-phone-line' },
            ].map((link) => (
              <Link
                key={link.path}
                to={link.path}
                className={`flex items-center gap-3 px-4 py-3 rounded-xl text-sm font-medium transition-all duration-200 cursor-pointer ${
                  location.pathname === link.path
                    ? 'bg-ea-blue/15 text-ea-blue border border-ea-blue/30'
                    : 'text-white/70 hover:text-white hover:bg-ea-dark-card'
                }`}
              >
                <div className="w-5 h-5 flex items-center justify-center">
                  <i className={`${link.icon} text-base`}></i>
                </div>
                {link.label}
              </Link>
            ))}

            {user ? (
              <>
                <Link
                  to="/dashboard"
                  className="flex items-center gap-3 px-4 py-3 rounded-xl text-sm font-medium text-ea-blue hover:bg-ea-blue/10 transition-all duration-200 cursor-pointer"
                >
                  <div className="w-5 h-5 flex items-center justify-center">
                    <i className="ri-dashboard-3-line text-base"></i>
                  </div>
                  My Dashboard
                </Link>
                <button
                  onClick={signOut}
                  className="flex items-center gap-3 px-4 py-3 rounded-xl text-sm font-medium text-white/70 hover:text-white hover:bg-ea-dark-card transition-all duration-200 cursor-pointer w-full text-left"
                >
                  <div className="w-5 h-5 flex items-center justify-center">
                    <i className="ri-logout-circle-line text-base"></i>
                  </div>
                  Sign Out
                </button>
              </>
            ) : (
              <>
                <Link
                  to="/signup"
                  className="flex items-center gap-3 px-4 py-3 rounded-xl text-sm font-medium text-white/70 hover:text-white hover:bg-ea-dark-card transition-all duration-200 cursor-pointer"
                >
                  <div className="w-5 h-5 flex items-center justify-center">
                    <i className="ri-user-line text-base"></i>
                  </div>
                  Sign In / Register
                </Link>
                <div className="pt-3">
                  <Link
                    to="/contact"
                    className="flex items-center justify-center gap-2 w-full px-5 py-3.5 bg-ea-blue hover:bg-ea-blue-light text-white text-sm font-semibold rounded-xl transition-all duration-300 whitespace-nowrap cursor-pointer"
                    style={{ boxShadow: '0 0 20px rgba(14, 165, 233, 0.25)' }}
                  >
                    <i className="ri-calendar-check-line"></i>
                    Book a Free Demo
                  </Link>
                </div>
              </>
            )}
          </div>
        </div>
      )}
    </header>
  );
};

export default Navbar;
